import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { UserRoles } from './models/enums/UserRoles';

import { AccountsMainComponent } from '@components/pages/accounts/accounts-main/accounts-main.component';
import { AccountsSingleComponent } from '@components/pages/accounts/accounts-single/accounts-single.component';
import { BuildingsMainComponent } from '@components/pages/buildings/buildings-main/buildings-main.component';
import { BuildingsSingleComponent } from '@components/pages/buildings/buildings-single/buildings-single.component';
import { CompaniesMainComponent } from '@components/pages/companies/companies-main/companies-main.component';
import { CompaniesSingleComponent } from '@components/pages/companies/companies-single/companies-single.component';
import { ConfigurationComponent } from '@components/pages/configuration/configuration.component';
import { CustomizationComponent } from '@components/pages/configuration/customization/customization.component';
import { ContactsMainComponent } from '@components/pages/contacts/contacts-main/contacts-main.component';
import { ContactsSingleComponent } from '@components/pages/contacts/contacts-single/contacts-single.component';
import { DashboardComponent } from '@components/pages/dashboard/dashboard.component';
import { NotificationsEditComponent } from '@components/pages/notifications/notifications-edit/notifications-edit.component';
import { NotificationsComponent } from '@components/pages/notifications/notifications.component';
import { OpportunitiesCreateComponent } from '@components/pages/opportunities/opportunities-create/opportunities-create.component';
import { OpportunitiesMainComponent } from '@components/pages/opportunities/opportunities-main/opportunities-main.component';
import { OpportunitiesSingleComponent } from '@components/pages/opportunities/opportunities-single/opportunities-single.component';
import { ReportsMainComponent } from '@components/pages/reports/reports-main/reports-main.component';
import { AccountService } from '@http/clients/account.service';
import { BuildingService } from '@http/clients/building.service';
import { CompanyService } from '@http/clients/company.service';
import { ContactService } from '@http/clients/contact.service';
import { OpportunityService } from '@http/clients/opportunity.service';
import { DEFAULT_CUSTOMIZATION_PATH } from '@models/customization.types';
import { entityDataResolver } from '@utils/resolvers/entity-data.resolver';
import { AccessDeniedComponent } from './components/pages/access-denied/access-denied.component';
import { AccountsEditComponent } from './components/pages/accounts/accounts-edit/accounts-edit.component';
import { BuildingsEditComponent } from './components/pages/buildings/buildings-edit/buildings-edit.component';
import { CompaniesEditComponent } from './components/pages/companies/companies-edit/companies-edit.component';
import { ContactsEditComponent } from './components/pages/contacts/contacts-edit/contacts-edit.component';
import { ForbiddenComponent } from './components/pages/errors/forbidden/forbidden.component';
import { NotFoundComponent } from './components/pages/errors/not-found/not-found.component';
import { ForgotPasswordComponent } from './components/pages/forgot-password/forgot-password.component';
import { LoginComponent } from './components/pages/login/login.component';
import { ReportSingleComponent } from './components/pages/reports/report-single/report-single.component';
import { WelcomeScreenComponent } from './components/pages/welcome-screen/welcome-screen.component';

export const routes: Routes = [
    { path: '', redirectTo: 'welcome', pathMatch: 'full' },
    { path: 'welcome', component: WelcomeScreenComponent },
    { path: 'login', component: LoginComponent },
    { path: 'forgot-password', component: ForgotPasswordComponent },
    { path: 'access-denied', component: AccessDeniedComponent },
    { path: 'forbidden', component: ForbiddenComponent, canActivate: [authGuard(UserRoles.None)] },
    { path: 'not-found', component: NotFoundComponent, canActivate: [authGuard(UserRoles.None)] },
    {
        path: 'dashboard',
        component: DashboardComponent,
        title: 'Dashboard',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'opportunities',
        component: OpportunitiesMainComponent,
        title: 'Opportunities',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'opportunities-create',
        component: OpportunitiesCreateComponent,
        title: 'New Opportunity',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'opportunities-single/:id/:tab',
        component: OpportunitiesSingleComponent,
        resolve: { entityData: entityDataResolver },
        data: { service: OpportunityService, property: 'name' },
        canActivate: [authGuard(UserRoles.None)],
        runGuardsAndResolvers: (from, to) => from.paramMap.get('id') !== to.paramMap.get('id')
    },
    {
        path: 'buildings',
        component: BuildingsMainComponent,
        title: 'Buildings',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'buildings-single/:id/:tab',
        component: BuildingsSingleComponent,
        resolve: { entityData: entityDataResolver },
        data: { service: BuildingService, property: 'name' },
        canActivate: [authGuard(UserRoles.None)],
        runGuardsAndResolvers: (from, to) => from.paramMap.get('id') !== to.paramMap.get('id')
    },
    {
        path: 'buildings-edit',
        component: BuildingsEditComponent,
        title: 'New Building',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'buildings-edit/:id',
        component: BuildingsEditComponent,
        resolve: { entityData: entityDataResolver },
        data: { service: BuildingService, property: 'name' },
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'companies',
        component: CompaniesMainComponent,
        title: 'Companies',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'companies-edit',
        component: CompaniesEditComponent,
        title: 'New Company',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'companies-edit/:id',
        component: CompaniesEditComponent,
        data: { service: CompanyService, property: 'name' },
        resolve: { entityData: entityDataResolver },
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'companies-single/:id/:tab',
        component: CompaniesSingleComponent,
        data: { service: CompanyService, property: 'name' },
        resolve: { entityData: entityDataResolver },
        canActivate: [authGuard(UserRoles.None)],
        runGuardsAndResolvers: (from, to) => from.paramMap.get('id') !== to.paramMap.get('id')
    },
    {
        path: 'accounts',
        component: AccountsMainComponent,
        title: 'Accounts',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'accounts-edit',
        component: AccountsEditComponent,
        title: 'New Account',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'accounts-edit/:id',
        component: AccountsEditComponent,
        resolve: { entityData: entityDataResolver },
        data: { service: AccountService, property: 'name' },
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'accounts-single/:id/:tab',
        component: AccountsSingleComponent,
        resolve: { entityData: entityDataResolver },
        data: { service: AccountService, property: 'name' },
        canActivate: [authGuard(UserRoles.None)],
        runGuardsAndResolvers: (from, to) => from.paramMap.get('id') !== to.paramMap.get('id')
    },
    {
        path: 'contacts',
        component: ContactsMainComponent,
        title: 'Contacts',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'contacts-edit',
        component: ContactsEditComponent,
        title: 'New Contact',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'contacts-edit/:id',
        component: ContactsEditComponent,
        data: { service: ContactService, property: 'fullName' },
        resolve: { entityData: entityDataResolver },
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'contacts-single/:id/:tab',
        component: ContactsSingleComponent,
        canActivate: [authGuard(UserRoles.None)],
        data: { service: ContactService, property: 'fullName' },
        resolve: { entityData: entityDataResolver },
        runGuardsAndResolvers: (from, to) => from.paramMap.get('id') !== to.paramMap.get('id')
    },
    {
        path: 'notifications',
        component: NotificationsComponent,
        title: 'Notifications',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'notifications-edit',
        component: NotificationsEditComponent,
        title: 'Notification Settings',
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'reports',
        component: ReportsMainComponent,
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'reports/:reportTemplate',
        component: ReportSingleComponent,
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'reports/:reportTemplate/:id',
        component: ReportSingleComponent,
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'reports/:reportTemplate/shared/:token',
        component: ReportSingleComponent,
        canActivate: [authGuard(UserRoles.None)]
    },
    {
        path: 'configuration',
        component: ConfigurationComponent,
        title: 'Customization',
        canActivate: [authGuard(UserRoles.SalesAdmin)],
        children: [
            {
                path: 'customization',
                canActivate: [authGuard(UserRoles.SalesAdmin)],
                children: [
                    {
                        path: ':entity',
                        component: CustomizationComponent,
                        canActivate: [authGuard(UserRoles.SalesAdmin)]
                    },
                    { path: '', pathMatch: 'full', redirectTo: DEFAULT_CUSTOMIZATION_PATH }
                ]
            },
            { path: '', pathMatch: 'full', redirectTo: 'customization' }
        ]
    },
    { path: '**', redirectTo: 'not-found', pathMatch: 'full' }
];
